import { Icon } from '@iconify/react'
import { useCallback, useState } from 'react'
import { PhoneWhatsAppLink } from '@/components/PhoneWhatsAppLink'
import { Select } from '@/components/Select'
import { useAuth } from '@/hooks/useAuth'
import { ApiClientError } from '@/lib/api/client'
import {
  fetchCampaignWinnerLookup,
  storeCampaignDrawWinner,
  type WinnerLookupSearchMode,
} from '@/lib/campaign-reports'
import type {
  CampaignDrawWinnerSavePayload,
  CampaignWinnerLookupPayload,
} from '@/types/campaign-report'
import styles from './WinnerLookupPanel.module.css'

interface WinnerLookupPanelProps {
  campaignId: number
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

const SEARCH_OPTIONS: { value: WinnerLookupSearchMode; label: string }[] = [
  { value: 'approximation', label: 'Aproximação' },
  { value: 'exact', label: 'Exata' },
]

function toDrawWinnerPayload(
  lookup: CampaignWinnerLookupPayload
): CampaignDrawWinnerSavePayload | null {
  if (!lookup.winner || lookup.match_type === 'none') {
    return null
  }
  return {
    lottery_number_normalized: lookup.lottery_number_normalized,
    lottery_value: lookup.lottery_value,
    search_mode: lookup.search_mode ?? 'approximation',
    match_type: lookup.match_type,
    winner: lookup.winner,
    candidates: lookup.candidates,
  }
}

function formatPurchaseAt(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${date} às ${time}`
}

export function WinnerLookupPanel({ campaignId }: WinnerLookupPanelProps) {
  const { token } = useAuth()
  const [query, setQuery] = useState('')
  const [searchMode, setSearchMode] = useState<WinnerLookupSearchMode>('approximation')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<CampaignWinnerLookupPayload | null>(null)
  const [saveLoading, setSaveLoading] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveDoneAt, setSaveDoneAt] = useState<string | null>(null)

  const handleSearch = useCallback(async () => {
    const trimmed = digitsOnly(query)
    if (!trimmed) {
      setError('Informe o número da cota.')
      setResult(null)
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)
    setSaveError(null)
    setSaveDoneAt(null)

    try {
      const data = await fetchCampaignWinnerLookup(campaignId, trimmed, token, searchMode)
      setResult(data)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível buscar. Tente novamente.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [campaignId, query, searchMode, token])

  const handleSaveWinner = useCallback(async () => {
    if (!result) return
    const body = toDrawWinnerPayload(result)
    if (!body) return

    setSaveLoading(true)
    setSaveError(null)
    setSaveDoneAt(null)

    try {
      const stored = await storeCampaignDrawWinner(campaignId, body, token)
      setSaveDoneAt(stored.saved_at)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível salvar. Tente novamente.'
      setSaveError(message)
    } finally {
      setSaveLoading(false)
    }
  }, [campaignId, result, token])

  return (
    <section className={styles.section} aria-label="Consulta por número da cota">
      <div className={styles.toolbar}>
        <div className={styles.toolbarInner}>
          <div className={styles.field}>
            <label htmlFor="winner-lookup-input">Número da cota</label>
            <div className={styles.inputPrefix}>
              <span className={styles.hash} aria-hidden>
                #
              </span>
              <input
                id="winner-lookup-input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className={styles.input}
                placeholder="Digite o número…"
                value={query}
                onChange={(event) => setQuery(digitsOnly(event.target.value))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void handleSearch()
                }}
              />
            </div>
          </div>
          <Select
            id="winner-search-mode"
            label="Tipo de busca"
            value={searchMode}
            onChange={(v) => setSearchMode(v as WinnerLookupSearchMode)}
            options={SEARCH_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            icon="mdi:target"
          />
          <button
            type="button"
            className={styles.searchBtn}
            onClick={() => void handleSearch()}
            disabled={loading}
          >
            <Icon
              icon={loading ? 'mdi:loading' : 'mdi:magnify'}
              className={loading ? styles.iconSpin : undefined}
              aria-hidden
            />
            {loading ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
      </div>

      {error ? (
        <p className={styles.alert} role="alert">
          {error}
        </p>
      ) : null}

      <div className={styles.resultArea}>
        {!result && !error && (
          <div className={styles.empty}>
            <span className={styles.emptyEmoji} aria-hidden>
              🕵️‍♂️
            </span>
            <p className={styles.emptyTitle}>Aguardando pesquisa</p>
            <p className={styles.emptyHint}>Informe o número da cota para localizar o ganhador.</p>
          </div>
        )}

        {result && result.match_type === 'none' && (
          <div className={styles.notFound}>
            <Icon icon="mdi:alert-circle-outline" className={styles.notFoundIcon} aria-hidden />
            <p>{result.message ?? 'Nenhum resultado.'}</p>
          </div>
        )}

        {result?.winner && (
          <article className={styles.winnerCard}>
            <header className={styles.winnerHeader}>
              <Icon icon="mdi:trophy" className={styles.winnerHeaderIcon} aria-hidden />
              <span>Ganhador encontrado</span>
            </header>
            <div className={styles.winnerBody}>
              <div className={styles.winnerBadgeCol}>
                <div className={styles.winnerNumberRing}>
                  <span className={styles.winnerNumberBig}>{result.winner.number}</span>
                </div>
                <span className={styles.winnerBadgeLabel}>Cota vencedora</span>
                {result.winner.match_type === 'approximation' && result.winner.distance > 0 ? (
                  <span className={styles.winnerDist}>
                    Ref. sorteio {result.lottery_number_normalized} · Δ {result.winner.distance}
                  </span>
                ) : null}
              </div>
              <dl className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <dt>
                    <Icon icon="mdi:account-outline" aria-hidden /> Nome
                  </dt>
                  <dd>{result.winner.donor.name ?? '—'}</dd>
                </div>
                <div className={styles.detailItem}>
                  <dt>
                    <Icon icon="mdi:email-outline" aria-hidden /> E-mail
                  </dt>
                  <dd>
                    {result.winner.donor.email ? (
                      <a className={styles.detailLink} href={`mailto:${result.winner.donor.email}`}>
                        {result.winner.donor.email}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div className={styles.detailItem}>
                  <dt>
                    <Icon icon="mdi:whatsapp" aria-hidden /> WhatsApp
                  </dt>
                  <dd>
                    <PhoneWhatsAppLink
                      phone={result.winner.donor.phone}
                      contactName={result.winner.donor.name ?? undefined}
                    />
                  </dd>
                </div>
                <div className={styles.detailItem}>
                  <dt>
                    <Icon icon="mdi:calendar-clock" aria-hidden /> Compra
                  </dt>
                  <dd>{result.winner.paid_at ? formatPurchaseAt(result.winner.paid_at) : '—'}</dd>
                </div>
                <div className={`${styles.detailItem} ${styles.detailSpan}`}>
                  <dt>
                    <Icon icon="mdi:bullhorn-outline" aria-hidden /> Campanha
                  </dt>
                  <dd>{result.winner.campaign_title ?? '—'}</dd>
                </div>
              </dl>
            </div>
          </article>
        )}

        {result?.winner ? (
          <div className={styles.saveFooter}>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={() => void handleSaveWinner()}
              disabled={saveLoading}
            >
              <Icon
                icon={saveLoading ? 'mdi:loading' : 'mdi:content-save-outline'}
                className={saveLoading ? styles.iconSpin : undefined}
                aria-hidden
              />
              {saveLoading ? 'Salvando…' : 'Salvar ganhador do sorteio'}
            </button>
            {saveError ? (
              <p className={styles.saveAlert} role="alert">
                {saveError}
              </p>
            ) : null}
            {saveDoneAt && !saveError ? (
              <p className={styles.saveOk}>Ganhador salvo para a página pública da campanha.</p>
            ) : null}
          </div>
        ) : null}

        {result?.winner && result.match_type === 'approximation' && (
          <div className={styles.candidates}>
            <span className={styles.candidatesLabel}>Vizinhos considerados</span>
            <ul className={styles.candidatesList}>
              <li>
                Acima:{' '}
                {result.candidates.superior
                  ? `${result.candidates.superior.number} (Δ ${result.candidates.superior.distance})`
                  : '—'}
              </li>
              <li>
                Abaixo:{' '}
                {result.candidates.inferior
                  ? `${result.candidates.inferior.number} (Δ ${result.candidates.inferior.distance})`
                  : '—'}
              </li>
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}
